import React from "react";
import { useSelector } from "react-redux";

import { untrack, useScoped } from "~/tools/impulse";
import { pluralize } from "~/tools/pluralize";
import { Template } from "~/tools/template";

import { selectTimestampDate } from "../../../../../app/state/view/timeline/TimelineSelectors";
import { useExplorationDrawerState } from "../../../../../components/react/drawer/use-exploration-drawer-state";
import { useTimelineInterval } from "../../../../../components/react/timeline/useTimelineInterval";
import { generatePath } from "../../../../../generic/routing";
import { COMPONENT_PATH } from "../../../../../routes/Routes";
import { EastPaneLayout } from "../EastPaneLayout";
import { EastPaneNotFound } from "../EastPaneNotFound";

import { Metric } from "./_metric";
import { MonitorStatus } from "./_monitor-status";
import { usePinnedItemsState } from "./_pinned-items-context";
import { PinnedItemsSection } from "./_pinned-items-section";

const pluralizeMonitorStatus = pluralize({
	1: "Monitor status",
	more: "Monitor statuses",
});

const pluralizeMetric = pluralize({
	1: "Metric",
	more: "Metrics",
});

export const PinnedItems: React.FC = () => {
	const explorationDrawer = useExplorationDrawerState();
	const timestamp = useSelector(selectTimestampDate);
	const timeRange = useTimelineInterval();
	const state = usePinnedItemsState();
	const isPinnedItemsEmpty = useScoped((scope) => state.size(scope) === 0, [state]);

	if (isPinnedItemsEmpty) {
		return <EastPaneNotFound type="PinnedItems" />;
	}

	return (
		<EastPaneLayout>
			<div>
				<PinnedItemsSection
					getCardComponentReference={({ component, topologyTime }) => ({
						...component,
						timestamp: topologyTime,
					})}
					getCardDescription={({ monitorDescription }) => monitorDescription}
					getCardTitle={({ message }) => message}
					name={pluralizeMonitorStatus}
					onEnlarge={({ timeRange: pinnedTimeRange, ...monitorStatus }) => {
						explorationDrawer.openMonitorStatus({
							identifier: monitorStatus.identifier,
							timestamp: monitorStatus.topologyTime,
							timeRange: untrack(pinnedTimeRange),
						});
					}}
					state={state.monitorStatuses}
				>
					{({ timeRange: pinnedTimeRange, ...monitorStatus }) => (
						<MonitorStatus monitorStatus={monitorStatus} timeRange={pinnedTimeRange} />
					)}
				</PinnedItemsSection>

				<PinnedItemsSection
					getCardComponentReference={({ component }) => component}
					getCardDescription={({ description }) => description}
					getCardTitle={({ name }) => name}
					isEnlargeVisible={({ chartType }) => chartType !== "Gauge"}
					name={pluralizeMetric}
					onAddToDashboard={(items) => {
						explorationDrawer.openAddToDashboard(
							items.map((item) => ({
								name: Template(item.name ?? ""),
								plugin: {
									kind: "TimeSeriesChart",
									spec: {
										yAxis: {
											format: item.unit == null ? undefined : { unit: item.unit },
										},
									},
									queries: item.metricQueries.map(({ promql, label, step }) => ({
										query: Template(promql),
										alias: label,
										step: step == null ? undefined : Template(step.toString()),
									})),
									links: [
										{
											url: Template(`/#${generatePath(COMPONENT_PATH, { identifier: item.component.identifier })}`),
											name: Template(`${item.component.name} (${item.component.type})`),
											targetBlank: false,
										},
									],
								},
							})),
						);
					}}
					onEnlarge={(metric) => {
						explorationDrawer.openMetrics({
							title: metric.name,
							unit: metric.unit,
							metricQueries: metric.metricQueries,
							componentIdOrIdentifier: metric.component.identifier,
							timeRange: timeRange.clone(),
							timestamp,
						});
					}}
					state={state.metrics}
				>
					{(metric) => <Metric markerTimestamp={timestamp} metric={metric} timeRange={timeRange} />}
				</PinnedItemsSection>
			</div>
		</EastPaneLayout>
	);
};
